import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, ShieldAlert, RotateCcw } from "lucide-react";

const SEVERITY = {
  mild: { color: "sky", Icon: Info, label: "Mild" },
  moderate: { color: "amber", Icon: AlertTriangle, label: "Moderate" },
  severe: { color: "red", Icon: ShieldAlert, label: "Severe" },
};

export default function Interactions() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setData(null);
    api.get("/interactions").then((r) => {
      if (r.data?.error) setError(r.data.error);
      else setData(r.data);
    }).catch((e) => setError(e.response?.data?.detail || "Failed to load"));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="p-10 max-w-5xl mx-auto">
        <Card className="rounded-3xl border-red-100 bg-red-50/30 p-6 text-red-700 text-sm">
          <p role="alert" data-testid="interactions-error">{error}</p>
          <Button variant="outline" onClick={load} className="mt-4 rounded-full" data-testid="interactions-retry">
            <RotateCcw className="w-4 h-4 mr-2" /> Try again
          </Button>
        </Card>
      </div>
    );
  }

  if (!data) {
    return <div role="status" data-testid="interactions-loading" className="p-10 text-slate-500">Analyzing your medications…</div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="mb-8 fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Drug-food interactions</h1>
        <p className="text-slate-500 mt-2">Foods and nutrients to watch out for based on your medications.</p>
      </div>

      <p className="text-sm text-slate-500 mb-5" data-testid="interactions-disclaimer">
        Prioritized highlights, not a comprehensive medication safety check. Confirm interactions with your pharmacist; do not change medication doses based on this review.
      </p>

      {data.summary && (
        <Card className="rounded-3xl border-sky-100 bg-sky-50/30 p-5 mb-6 fade-up delay-1">
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="interactions-summary">{data.summary}</p>
        </Card>
      )}

      {!data.interactions || data.interactions.length === 0 ? (
        <Card className="rounded-3xl border-emerald-100 p-12 text-center fade-up delay-2">
          <p className="text-slate-500 text-sm" data-testid="interactions-empty">No interaction highlights to display. This does not confirm that every food is safe with your medicines.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.interactions.map((it, i) => {
            const sev = SEVERITY[it.severity] || SEVERITY.mild;
            const SevIcon = sev.Icon;
            return (
              <Card key={i} className={`rounded-2xl border-${sev.color}-100 p-5 bg-white fade-up delay-${Math.min(i + 1, 5)}`} data-testid={`interaction-${i}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-${sev.color}-50 flex items-center justify-center flex-shrink-0`}>
                    <SevIcon className={`w-5 h-5 text-${sev.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <div className="font-display font-semibold text-slate-900" data-testid={`interaction-name-${i}`}>
                        {it.medication} <span className="text-slate-400 font-normal">×</span> {it.food_or_nutrient}
                      </div>
                      <Badge data-testid={`interaction-severity-${i}`} className={`bg-${sev.color}-50 text-${sev.color}-700 border-${sev.color}-100 rounded-full`}>
                        {sev.label}
                      </Badge>
                    </div>
                    <p data-testid={`interaction-effect-${i}`} className="text-sm text-slate-600 leading-relaxed">{it.effect}</p>
                    {it.recommendation && (
                      <div data-testid={`interaction-recommendation-${i}`} className="mt-3 text-xs bg-emerald-50/60 text-emerald-800 rounded-xl p-3">
                        <span className="font-semibold">Recommendation:</span> {it.recommendation}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
